import { useParams } from "react-router-dom";
import moment from "moment";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import NumberFormat from "../../../components/NumberFormat";
import CardForm from "../../../components/CardForm";
import TableListTitle from "../../../components/TableList/TableListTitle";
import {
  Quotation,
  QuotationStatus,
  SetttingGroup,
  useAllGroupSettingsQuery,
  useCancelQuotationMutation,
  useConfirmQuotationMutation,
  useGetQuotationQuery,
} from "../../../generated/graphql";
import { BlobProvider } from "@react-pdf/renderer";
import PrintQuotation from "../PrintQuotation";
import { Fragment, useEffect, useMemo, useState } from "react";
import DetailLine from "../../../components/DetailLine";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  getQuotationStatusColorScheme,
  getQuotationStatusName,
} from "../../../helpers/global";
import RouterLink from "../../../components/RouterLink";
import { getMenusWithDivider } from "../../../helpers/menu";
import { useConfirmation } from "../../../contexts/ConfirmationContext";
import update from "immutability-helper";
import QuotationForm from "../QuotationForm";
import { FormMutationType } from "../../../components/FullWidthModal/interface";

const QuotationDetail = () => {
  const { quotationId } = useParams();

  const { data, loading, refetch } = useGetQuotationQuery({
    variables: { id: quotationId as string },
  });

  const { data: dataSettings } = useAllGroupSettingsQuery({
    variables: { group: SetttingGroup.QuotationSetting },
  });

  const [quotation, setQuotation] = useState<Quotation | null>(null);

  useEffect(() => {
    setQuotation((data?.getQuotation as Quotation) || null);
  }, [data]);

  const { confirmation, setIsLoading, close } = useConfirmation();

  const [confirmQuotation] = useConfirmQuotationMutation();

  const onConfirm = () => {
    confirmation({
      description: "Are you sure want to confirm this quotation?",
      callback: async () => {
        try {
          setIsLoading(true);
          const res = await confirmQuotation({
            variables: {
              id: quotationId as string,
            },
          });
          setQuotation(
            update(quotation, {
              status: {
                $set: res.data?.confirmQuotation.status as QuotationStatus,
              },
              confirmedAt: { $set: res.data?.confirmQuotation.confirmedAt },
            })
          );
        } catch (e) {
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [cancelQuotation] = useCancelQuotationMutation();

  const onCancel = () => {
    confirmation({
      description: "Are you sure want to cancel this quotation?",
      callback: async () => {
        try {
          setIsLoading(true);
          const res = await cancelQuotation({
            variables: {
              id: quotationId as string,
            },
          });
          setQuotation(
            update(quotation, {
              status: {
                $set: res.data?.cancelQuotation.status as QuotationStatus,
              },
              canceledAt: { $set: res.data?.cancelQuotation.canceledAt },
            })
          );
        } catch (e) {
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  const menuPrint = useMemo(
    () => (
      <BlobProvider
        document={
          <PrintQuotation
            quotation={quotation as Quotation}
            settings={dataSettings?.allGroupSettings}
          />
        }
      >
        {({ url }) => (
          <MenuItem as={Link} href={url || ""} target="_blank" size="xs">
            Print
          </MenuItem>
        )}
      </BlobProvider>
    ),
    [dataSettings, quotation]
  );

  const actionMenuItems = () => {
    const menus = [];

    if (data) {
      menus.push(menuPrint);
      // menus.push(
      //   <BlobProvider
      //     document={
      //       <PrintQuotation
      //         quotation={quotation as Quotation}
      //         settings={dataSettings?.allGroupSettings}
      //       />
      //     }
      //   >
      //     {({ url }) => (
      //       <MenuItem as={Link} href={url || ""} target="_blank" size="xs">
      //         Print
      //       </MenuItem>
      //     )}
      //   </BlobProvider>
      // );
    }

    if (quotation?.status === QuotationStatus.Hold) {
      menus.push(<MenuItem onClick={onOpenForm}>Edit</MenuItem>);
      menus.push(<MenuItem onClick={onConfirm}>Confirm</MenuItem>);
    }

    if (quotation?.status !== QuotationStatus.Canceled) {
      menus.push(<MenuItem onClick={onCancel}>Cancel</MenuItem>);
    }

    return getMenusWithDivider(menus);
  };

  if (loading) {
    return (
      <Flex alignItems="center" justifyContent="center" minH="200px" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      <CardForm maxW="container.xl">
        <TableListTitle
          title="Quotation Detail"
          subtitleComponent={
            <Box
              color="#717A7A"
              fontWeight="500"
              fontSize="14px"
              lineHeight="1.6"
              mt={2}
              alignItems="center"
            >
              <Box mb={1}>
                Status:{" "}
                <Badge
                  colorScheme={getQuotationStatusColorScheme(
                    quotation?.status as QuotationStatus
                  )}
                >
                  {getQuotationStatusName(quotation?.status as QuotationStatus)}
                </Badge>
              </Box>
            </Box>
          }
          actionComponents={
            <Flex gap={5}>
              <Button as={RouterLink} size="sm" variant="link" to="/quotation">
                Back
              </Button>
              <Menu placement="bottom-end">
                <MenuButton
                  size="sm"
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                >
                  Action
                </MenuButton>
                <MenuList>
                  {actionMenuItems().map((elm, index) => (
                    <Fragment key={`action-${index}`}>{elm}</Fragment>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          }
        />

        <Box>
          <DetailLine
            title="Quotation Info"
            mb={10}
            items={[
              {
                label: "Booking Code",
                value: quotation?.bookingCode || "",
              },
              {
                label: "Itinerary",
                value: quotation?.itinerary || "",
              },
              {
                label: "Itinerary Date",
                value: moment(quotation?.itineraryDate).format(
                  "dddd, D/MMM/YYYY"
                ),
              },
              {
                label: "To",
                value: quotation?.to || "",
              },
              {
                label: "Confirmed At",
                value: quotation?.confirmedAt
                  ? moment(quotation?.confirmedAt).format("DD MMMM, YYYY")
                  : "-",
              },
              {
                label: "Canceled At",
                value: quotation?.canceledAt
                  ? moment(quotation?.canceledAt).format("DD MMMM, YYYY")
                  : "-",
              },
            ]}
          />

          <DetailLine
            title="Signature Info"
            items={[
              {
                label: "Signature Name",
                value: quotation?.signature?.name || "",
              },
              {
                label: "Signature Image",
                value: (
                  <Image
                    src={quotation?.signature?.imageUrl}
                    w="auto"
                    h="80px"
                  />
                ),
              },
              {
                label: "Signature Background",
                value: quotation?.signatureBackground?.name || "",
              },
              {
                label: "Image",
                value: (
                  <Image
                    src={quotation?.signatureBackground?.imageUrl}
                    w="auto"
                    h="80px"
                  />
                ),
              },
            ]}
          />

          <Box mt={3}>
            <Heading mb={2} fontSize="xl">
              Quotation Items
            </Heading>
            <VStack alignItems="inherit" gap={4} divider={<Divider />}>
              {quotation?.quotationItems.map((elm, index) => (
                <Box key={index}>
                  <DetailLine
                    items={[
                      {
                        label: "Number of Pax",
                        value: elm.numberOfPax.toString(),
                      },
                      {
                        label: "Price / Pax",
                        value: (
                          <NumberFormat
                            prefix="$ "
                            value={elm?.pricePerPax || 0}
                          />
                        ),
                      },
                      {
                        label: "Airline",
                        value: elm.airline,
                      },
                      {
                        label: "Class",
                        value: elm.class,
                      },
                      {
                        label: "Pessanger Name",
                        value: elm.passengers.join(", "),
                      },
                    ]}
                  />
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>No. Flight</Th>
                        <Th>Travel</Th>
                        <Th>Depart Time</Th>
                        <Th>Arrival Time</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {elm.quotationItemFlights.map((flight, index) => (
                        <Tr key={`${index}`}>
                          <Td>{flight.travel}</Td>
                          <Td>{flight.travel}</Td>
                          <Td>
                            {moment(flight.departTime).format(
                              "dddd, D/MMM/YYYY HH:mm"
                            )}
                          </Td>
                          <Td>
                            {moment(flight.arrivalTime).format(
                              "dddd, D/MMM/YYYY HH:mm"
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ))}
            </VStack>
          </Box>

          <DetailLine
            mt={10}
            title="Billings"
            items={[
              {
                label: "Subtotal",
                value: <NumberFormat prefix="$ " value={quotation?.subTotal} />,
              },
              {
                label: "Eddition Costa",
                value: (
                  <NumberFormat prefix="$ " value={quotation?.additionalCost} />
                ),
              },
              {
                label: "NTA",
                value: <NumberFormat prefix="$ " value={quotation?.total} />,
              },
            ]}
          />
        </Box>
      </CardForm>
      <QuotationForm
        isOpen={isOpenForm}
        onClose={(res) => {
          setQuotation({ ...(quotation as Quotation), ...res?.data });
          onCloseForm();
          if (res?.type === FormMutationType.UPDATE) {
            refetch();
          }
        }}
        id={quotation?.id}
      />
    </>
  );
};

export default QuotationDetail;
